// tasks — the team Tasks board (pages/TaskBoard.tsx), migration 0091.
//
// One place to see who is working on what: status columns, cards dragged
// between them, an assignee and a priority per card, comments with @mentions,
// a diary of every status / assignee / priority change, and a notification
// row for the person told about it. Supabase only — the page shows a notice
// in Firestore mode.
//
// Everything that decides something (positions, diffs, who gets told, what a
// mention is) is a pure function here so it can be tested without a database.
import { supabase, USE_SUPABASE, subscribeTable, isoFromMs, msFromISO0 } from './supabaseClient';
import { subscribeTableHub } from './tableHub';
import { userTokenName } from './orderMentions';
import { UserRole, type User } from '../types';
import { isSuperTier } from './roles';

export { USE_SUPABASE };

// ── Types ──────────────────────────────────────────────────────────────────

export type TaskPriority = 'lowest' | 'low' | 'medium' | 'high' | 'highest';
export const PRIORITIES: readonly TaskPriority[] = ['lowest', 'low', 'medium', 'high', 'highest'];

/** How each priority is drawn: a label, a rank for sorting, and colours. */
export const PRIORITY_META: Record<TaskPriority, { label: string; rank: number; chip: string; dot: string; bar: string }> = {
  lowest: { label: 'Lowest', rank: 0, chip: 'bg-slate-100 text-slate-600 ring-slate-200', dot: 'bg-slate-400', bar: 'border-l-slate-300' },
  low: { label: 'Low', rank: 1, chip: 'bg-sky-50 text-sky-700 ring-sky-200', dot: 'bg-sky-500', bar: 'border-l-sky-400' },
  medium: { label: 'Medium', rank: 2, chip: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-500', bar: 'border-l-amber-400' },
  high: { label: 'High', rank: 3, chip: 'bg-orange-50 text-orange-700 ring-orange-200', dot: 'bg-orange-500', bar: 'border-l-orange-500' },
  highest: { label: 'Highest', rank: 4, chip: 'bg-red-50 text-red-700 ring-red-200', dot: 'bg-red-500', bar: 'border-l-red-500' },
};

export const isPriority = (v: unknown): v is TaskPriority => PRIORITIES.includes(v as TaskPriority);

export interface TaskColumn {
  id: string;
  name: string;
  position: number;
  createdAt: number;
  /** The board the column belongs to (0109); null = shared by every board (before 0109). */
  boardId: string | null;
}

/** One person on a task. */
export interface TaskAssignee { id: string; name: string }

export interface Task {
  id: string;
  number: number;
  key: string; // TASK-12
  title: string;
  description: string;
  columnId: string;
  /** The board the task is on (0108); 'general' for a row read before 0108. */
  boardId: string;
  position: number;
  /** Everyone on the task, in the order they were added (0103, 2026-09-26). */
  assignees: TaskAssignee[];
  /** The first of `assignees`, kept for readers of the old single field. */
  assigneeId: string | null;
  assigneeName: string | null;
  priority: TaskPriority;
  dueDate: string | null; // YYYY-MM-DD
  labels: string[];
  archived: boolean;
  archivedAt: number | null;
  archivedBy: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface TaskComment {
  id: string;
  taskId: string;
  authorId: string;
  authorName: string;
  body: string;
  mentions: string[]; // user ids
  createdAt: number;
  editedAt: number | null;
}

export type TaskActivityKind =
  | 'created' | 'status' | 'assignee' | 'priority' | 'title' | 'due'
  | 'description' | 'labels' | 'archived' | 'restored' | 'comment'
  | 'attachment' // to = file added, from = file removed (services/taskAttachments)
  | 'board'; // moved to another board (0108): from / to = board names

export interface TaskActivity {
  id: string;
  taskId: string;
  actorId: string | null;
  actorName: string | null;
  kind: TaskActivityKind;
  from: string | null;
  to: string | null;
  createdAt: number;
}

export type TaskNotificationKind = 'assigned' | 'mentioned' | 'commented';

export interface TaskNotification {
  id: string;
  recipientId: string;
  taskId: string;
  kind: TaskNotificationKind;
  actorId: string | null;
  actorName: string | null;
  title: string;
  body: string;
  read: boolean;
  createdAt: number;
}

export interface Actor {
  id: string;
  name: string;
}

/** The fields a person fills in when creating or editing a task. */
export interface TaskInput {
  title: string;
  description: string;
  columnId: string;
  assigneeIds: string[];
  priority: TaskPriority;
  dueDate: string | null;
  labels: string[];
  /** The board a new task goes on (0108); left out before 0108, when there are no boards. */
  boardId?: string;
}

/** Who has the Tasks page (sidebar + route). Drivers and stores work from
 *  the phone / store hub and do not see the board (client, 2026-10-12: drivers
 *  must not see or manage tasks — they were added 2026-09-25 and are out again);
 *  nobody assigns them a task and no task push is sent to them (`TASK_BLIND_ROLES`). */
export const TASK_BOARD_ROLES: readonly UserRole[] = [
  UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER,
  UserRole.STOCK_MANAGER, UserRole.WAREHOUSE_MANAGER, UserRole.WORKER, UserRole.PACKAGER,
  UserRole.TEAM_MEMBER,
];

/** Roles that never take part in tasks: not on the board, not in the assignee
 *  picker, not told by push. Mirrored in netlify/functions/push-dispatch.js. */
export const TASK_BLIND_ROLES: readonly UserRole[] = [UserRole.DRIVER, UserRole.STORE];

export const isTaskAdmin = (role: UserRole | string | undefined | null): boolean =>
  role === UserRole.ADMIN || isSuperTier(role);

// ── Keys, positions, mentions, diffs (pure) ────────────────────────────────

export const taskKey = (n: number): string => `TASK-${n}`;

/** "TASK-12", "task-12", "#12" or "12" -> 12; anything else -> null. */
export const parseTaskKey = (s: string | null | undefined): number | null => {
  const m = String(s || '').trim().match(/^(?:#|task-?)?(\d{1,9})$/i);
  return m ? Number(m[1]) : null;
};

/** A position strictly between two neighbours (either side open). Cards are
 *  ordered by a float so a move rewrites ONE row, never the whole column. */
export const positionBetween = (before: number | undefined, after: number | undefined): number => {
  if (before === undefined && after === undefined) return Date.now();
  if (before === undefined) return (after as number) - 1;
  if (after === undefined) return before + 1;
  return (before + after) / 2;
};

/** The users a comment names with @First (token = first word of the name,
 *  the same grammar the chat uses). Case-insensitive; each user once. */
export const extractMentionedUsers = (body: string, users: readonly User[]): User[] => {
  const tokens = new Set<string>();
  const re = /(^|[^\w@])@([A-Za-z0-9-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body || '')) !== null) tokens.add(m[2].toLowerCase());
  if (!tokens.size) return [];
  const out: User[] = [];
  for (const u of users) {
    if (u.isArchived || u.placeholder) continue;
    const t = userTokenName(u.name).toLowerCase();
    if (t && tokens.has(t) && !out.some((x) => x.id === u.id)) out.push(u);
  }
  return out;
};

export interface TaskDiff {
  kind: TaskActivityKind;
  from: string | null;
  to: string | null;
}

/** What changed between two versions of a task, as diary lines. Column ids
 *  are translated to names through `columnName` so the log reads
 *  "To Do -> In Progress", not two ids. */
export const taskDiffs = (
  before: Task,
  after: Task,
  columnName: (id: string) => string = (id) => id,
): TaskDiff[] => {
  const out: TaskDiff[] = [];
  if (before.columnId !== after.columnId) out.push({ kind: 'status', from: columnName(before.columnId), to: columnName(after.columnId) });
  if (assigneeKey(before.assignees) !== assigneeKey(after.assignees)) {
    out.push({ kind: 'assignee', from: assigneeNames(before.assignees), to: assigneeNames(after.assignees) });
  }
  if (before.priority !== after.priority) out.push({ kind: 'priority', from: PRIORITY_META[before.priority].label, to: PRIORITY_META[after.priority].label });
  if (before.title !== after.title) out.push({ kind: 'title', from: before.title, to: after.title });
  if ((before.dueDate || null) !== (after.dueDate || null)) out.push({ kind: 'due', from: before.dueDate || null, to: after.dueDate || null });
  if (before.description !== after.description) out.push({ kind: 'description', from: null, to: null });
  if (before.labels.join(' ') !== after.labels.join(' ')) out.push({ kind: 'labels', from: before.labels.join(', ') || null, to: after.labels.join(', ') || null });
  return out;
};

/** Who is told about a comment: every mentioned user, then every assignee
 *  (as "commented") unless they were mentioned already, and never the
 *  author. Pure, so the rule is testable. */
export const commentRecipients = (input: {
  authorId: string;
  assigneeIds: readonly string[];
  mentionedIds: readonly string[];
}): { userId: string; kind: TaskNotificationKind }[] => {
  const out: { userId: string; kind: TaskNotificationKind }[] = [];
  const seen = new Set<string>();
  for (const id of input.mentionedIds) {
    if (id === input.authorId || seen.has(id)) continue;
    seen.add(id);
    out.push({ userId: id, kind: 'mentioned' });
  }
  for (const a of input.assigneeIds) {
    if (!a || a === input.authorId || seen.has(a)) continue;
    seen.add(a);
    out.push({ userId: a, kind: 'commented' });
  }
  return out;
};

// ── Assignees (0103): a list, the first mirrored into the old single field ──

/** As many people as a task may carry (0103's check constraint says the same). */
export const TASK_ASSIGNEES_MAX = 20;

/** The ids of a list, joined — equal keys mean the same people in the same order. */
export const assigneeKey = (list: readonly TaskAssignee[]): string => list.map((a) => a.id).join('|');

/** "Ada, Bob" for a diary line; null for nobody. */
export const assigneeNames = (list: readonly TaskAssignee[]): string | null =>
  list.map((a) => a.name).filter(Boolean).join(', ') || null;

/** Ids → people, in order, no repeats: the name from the users list, else the
 *  name the task already held for that id (an archived user), else ''. */
export const resolveAssignees = (users: readonly User[], ids: readonly string[], known: readonly TaskAssignee[] = []): TaskAssignee[] => {
  const out: TaskAssignee[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: users.find((u) => u.id === id)?.name ?? known.find((a) => a.id === id)?.name ?? '' });
  }
  return out;
};

/** The row columns for a list: the arrays, and the old pair = the first. */
const assigneeColumns = (list: readonly TaskAssignee[]) => ({
  assignee_ids: list.map((a) => a.id),
  assignee_names: list.map((a) => a.name),
  assignee_id: list[0]?.id ?? null,
  assignee_name: list[0]?.name ?? null,
});
/** Before 0103 is applied the arrays are unknown to PostgREST; the write is
 *  retried with the old pair alone (the first person), so the board keeps
 *  working through the rollout. */
const missingAssigneeArrays = (error: { message?: string } | null | undefined): boolean =>
  /assignee_(ids|names)/.test(error?.message ?? '');
const withoutAssigneeArrays = <T extends Record<string, unknown>>(row: T): Omit<T, 'assignee_ids' | 'assignee_names'> => {
  const { assignee_ids: _i, assignee_names: _n, ...rest } = row as any;
  return rest;
};

/** Labels typed as "urgent, client, bug" -> ['urgent', 'client', 'bug']. */
/** The card's one-glance preview of a description: the markdown markers the
 *  editor understands are dropped (**bold**, _italic_, "- " bullets), lines
 *  are joined with a space, and the text is cut at a word boundary to `max`
 *  characters with an ellipsis. '' when there is nothing to show. (Client
 *  ask, 2026-09-26: a card should show that a task carries instructions, not
 *  just its title; the dialog still shows the whole description.) */
export const descriptionPreview = (text: string | null | undefined, max = 160): string => {
  const plain = (text || '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|[^\w])_(.+?)_(?=[^\w]|$)/g, '$1$2')
    .split('\n')
    .map((l) => l.replace(/^\s*[-*]\s+/, '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) return plain;
  const head = plain.slice(0, max + 1);
  const at = head.lastIndexOf(' ');
  return `${(at > max * 0.6 ? head.slice(0, at) : plain.slice(0, max)).trimEnd()}…`;
};

export const parseLabels = (raw: string): string[] => {
  const out: string[] = [];
  for (const l of (raw || '').split(/[,\n]/)) {
    const t = l.trim();
    if (t && !out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
  }
  return out;
};

// ── Row mappers ────────────────────────────────────────────────────────────

export const columnFromRow = (r: any): TaskColumn => ({
  id: String(r.id),
  name: r.name ?? '',
  position: Number(r.position) || 0,
  createdAt: msFromISO0(r.created_at),
  boardId: r.board_id != null ? String(r.board_id) : null,
});

/** The list from a row: the arrays when the row has them (0103), else the
 *  old pair as a list of one. */
const assigneesFromRow = (r: any): Pick<Task, 'assignees' | 'assigneeId' | 'assigneeName'> => {
  const ids: string[] = Array.isArray(r.assignee_ids) ? r.assignee_ids.map(String) : r.assignee_id ? [String(r.assignee_id)] : [];
  const names: string[] = Array.isArray(r.assignee_names) ? r.assignee_names.map((n: any) => String(n ?? '')) : r.assignee_name != null ? [String(r.assignee_name)] : [];
  const assignees = ids.map((id, i) => ({ id, name: names[i] ?? (id === r.assignee_id ? String(r.assignee_name ?? '') : '') }));
  return { assignees, assigneeId: assignees[0]?.id ?? null, assigneeName: assignees[0]?.name ?? null };
};

export const taskFromRow = (r: any): Task => ({
  id: String(r.id),
  number: Number(r.number) || 0,
  key: taskKey(Number(r.number) || 0),
  title: r.title ?? '',
  description: r.description ?? '',
  columnId: String(r.column_id),
  boardId: r.board_id ? String(r.board_id) : GENERAL_BOARD,
  position: Number(r.position) || 0,
  ...assigneesFromRow(r),
  priority: isPriority(r.priority) ? r.priority : 'medium',
  dueDate: r.due_date ? String(r.due_date).slice(0, 10) : null,
  labels: Array.isArray(r.labels) ? r.labels.map(String) : [],
  archived: !!r.archived,
  archivedAt: r.archived_at ? msFromISO0(r.archived_at) : null,
  archivedBy: r.archived_by ?? null,
  createdBy: r.created_by ?? null,
  createdByName: r.created_by_name ?? null,
  createdAt: msFromISO0(r.created_at),
  updatedAt: msFromISO0(r.updated_at),
});

export const commentFromRow = (r: any): TaskComment => ({
  id: String(r.id),
  taskId: String(r.task_id),
  authorId: String(r.author_id ?? ''),
  authorName: r.author_name ?? '',
  body: r.body ?? '',
  mentions: Array.isArray(r.mentions) ? r.mentions.map(String) : [],
  createdAt: msFromISO0(r.created_at),
  editedAt: r.edited_at ? msFromISO0(r.edited_at) : null,
});

export const activityFromRow = (r: any): TaskActivity => ({
  id: String(r.id),
  taskId: String(r.task_id),
  actorId: r.actor_id ?? null,
  actorName: r.actor_name ?? null,
  kind: r.kind as TaskActivityKind,
  from: r.from_value ?? null,
  to: r.to_value ?? null,
  createdAt: msFromISO0(r.created_at),
});

export const notificationFromRow = (r: any): TaskNotification => ({
  id: String(r.id),
  recipientId: String(r.recipient_id),
  taskId: String(r.task_id),
  kind: r.kind as TaskNotificationKind,
  actorId: r.actor_id ?? null,
  actorName: r.actor_name ?? null,
  title: r.title ?? '',
  body: r.body ?? '',
  read: !!r.read,
  createdAt: msFromISO0(r.created_at),
});

const fail = (where: string, error: { message: string } | null): never => {
  throw new Error(`${where}: ${error?.message || 'unknown error'}`);
};

// ── Live reads ─────────────────────────────────────────────────────────────

export const subscribeTaskColumns = (onRows: (rows: TaskColumn[]) => void, onError?: (e: Error) => void) =>
  subscribeTable<TaskColumn>('task_columns', columnFromRow, onRows, { order: { column: 'position' }, onError });

/** The board follows LIVE tasks only (`archived = false`): an archived row
 *  leaves the list the moment it is archived, a restored one comes back.
 *  Archived tasks are never streamed — they grow forever; the Archived view
 *  reads a page of them on demand (fetchArchivedTasks). */
export const subscribeTasks = (onRows: (rows: Task[]) => void, onError?: (e: Error) => void) =>
  subscribeTable<Task>('tasks', taskFromRow, onRows, {
    order: { column: 'position' },
    eq: [{ column: 'archived', value: false }],
    onError,
  });

export const ARCHIVE_PAGE = 200;

/** The newest archived tasks, one page. */
export async function fetchArchivedTasks(limit: number = ARCHIVE_PAGE): Promise<Task[]> {
  const { data, error } = await supabase().from('tasks').select('*').eq('archived', true)
    .order('archived_at', { ascending: false }).limit(limit);
  if (error) return fail('fetchArchivedTasks', error);
  return (data || []).map(taskFromRow);
}

/** One task by id, live or archived (a deep link to an archived task). */
export async function fetchTask(id: string): Promise<Task | null> {
  const { data, error } = await supabase().from('tasks').select('*').eq('id', id).maybeSingle();
  if (error) return fail('fetchTask', error);
  return data ? taskFromRow(data) : null;
}

/** One task by its number (TASK-68 -> 68), live or archived: a shared link
 *  /tasks?task=TASK-68 names the task by key, and the key must still open it
 *  after the task is archived off the live board. */
export async function fetchTaskByNumber(n: number): Promise<Task | null> {
  const { data, error } = await supabase().from('tasks').select('*').eq('number', n).maybeSingle();
  if (error) return fail('fetchTaskByNumber', error);
  return data ? taskFromRow(data) : null;
}

/** The shareable link to a task: /tasks?task=TASK-68 (the key, not the id —
 *  readable, and the same for everyone). Absolute when `origin` is given. */
export const taskLink = (key: string, origin: string = ''): string => `${origin}/tasks?task=${encodeURIComponent(key)}`;

/** One task's rows from a child table: a plain read, re-read on every change
 *  to that task (the hub channel is filtered to it). Not subscribeTable: its
 *  cache is per table, and task A's comments must never paint under task B. */
export const CHILD_ROWS_CAP = 300;

function subscribeTaskChild<T>(
  table: 'task_comments' | 'task_activity',
  taskId: string,
  map: (r: any) => T,
  ascending: boolean,
  onRows: (rows: T[]) => void,
  onError?: (e: Error) => void,
): () => void {
  let dead = false;
  const load = async () => {
    const { data, error } = await supabase().from(table).select('*').eq('task_id', taskId)
      .order('created_at', { ascending }).limit(CHILD_ROWS_CAP);
    if (dead) return;
    if (error) { onError?.(new Error(error.message)); return; }
    onRows((data || []).map(map));
  };
  void load();
  const off = subscribeTableHub(
    table,
    {
      onChange: (p: any) => {
        const row = p?.new ?? p?.old;
        if (row && String(row.task_id) !== taskId) return;
        void load();
      },
      onResync: () => { void load(); },
    },
    { filter: `task_id=eq.${taskId}` },
  );
  return () => { dead = true; off(); };
}

export const subscribeTaskComments = (taskId: string, onRows: (rows: TaskComment[]) => void, onError?: (e: Error) => void) =>
  subscribeTaskChild('task_comments', taskId, commentFromRow, true, onRows, onError);

export const subscribeTaskActivity = (taskId: string, onRows: (rows: TaskActivity[]) => void, onError?: (e: Error) => void) =>
  subscribeTaskChild('task_activity', taskId, activityFromRow, false, onRows, onError);

/** New notifications for one person, as they land; no history replay. The
 *  channel is filtered to the recipient (a row never changes recipient, so
 *  the filter is safe) and only INSERT is bound. */
export const subscribeMyTaskNotifications = (userId: string, onNew: (n: TaskNotification) => void): (() => void) =>
  subscribeTableHub(
    'task_notifications',
    {
      events: ['INSERT'],
      onChange: (p: any) => {
        const row = p?.new;
        if (!row || String(row.recipient_id) !== userId) return;
        onNew(notificationFromRow(row));
      },
    },
    { filter: `recipient_id=eq.${userId}` },
  );

// ── Writes ─────────────────────────────────────────────────────────────────

/** One insert for however many diary lines a change produced. */
const logActivity = async (taskId: string, actor: Actor, diffs: TaskDiff | TaskDiff[]): Promise<void> => {
  const rows = (Array.isArray(diffs) ? diffs : [diffs]).map((d) => ({
    task_id: taskId, actor_id: actor.id, actor_name: actor.name, kind: d.kind, from_value: d.from, to_value: d.to,
  }));
  if (!rows.length) return;
  const { error } = await supabase().from('task_activity').insert(rows);
  if (error) console.warn('task activity not written:', error.message);
};

const notificationTitle = (kind: TaskNotificationKind, task: Pick<Task, 'key'>, actor: Actor): string =>
  kind === 'assigned'
    ? `${task.key} assigned to you`
    : kind === 'mentioned'
      ? `${actor.name} mentioned you on ${task.key}`
      : `New comment on ${task.key}`;

/** One insert for every person told; the author is never a recipient. */
const notify = async (
  recipients: { userId: string; kind: TaskNotificationKind }[],
  task: Pick<Task, 'id' | 'key' | 'title'>,
  actor: Actor,
  body: string,
): Promise<void> => {
  const rows = recipients
    .filter((r) => r.userId && r.userId !== actor.id)
    .map((r) => ({
      recipient_id: r.userId, task_id: task.id, kind: r.kind, actor_id: actor.id, actor_name: actor.name,
      title: notificationTitle(r.kind, task, actor).slice(0, 200), body: body.slice(0, 1000),
    }));
  if (!rows.length) return;
  const { error } = await supabase().from('task_notifications').insert(rows);
  if (error) console.warn('task notification not written:', error.message);
};

export async function createTask(input: TaskInput, actor: Actor, users: readonly User[]): Promise<Task> {
  const assignees = resolveAssignees(users, input.assigneeIds);
  const row = {
    title: input.title.trim(),
    description: input.description,
    column_id: input.columnId,
    position: Date.now(),
    ...assigneeColumns(assignees),
    priority: input.priority,
    due_date: input.dueDate,
    labels: input.labels,
    ...(input.boardId ? { board_id: input.boardId } : {}),
    created_by: actor.id,
    created_by_name: actor.name,
  };
  let res = await supabase().from('tasks').insert(row).select('*').single();
  if (res.error && missingAssigneeArrays(res.error)) res = await supabase().from('tasks').insert(withoutAssigneeArrays(row)).select('*').single();
  const { data, error } = res;
  if (error || !data) return fail('createTask', error);
  const task = taskFromRow(data);
  await logActivity(task.id, actor, { kind: 'created', from: null, to: task.title });
  const told = task.assignees.map((a) => ({ userId: a.id, kind: 'assigned' as const }));
  if (told.length) await notify(told, task, actor, `${actor.name} assigned you "${task.title}"`);
  return task;
}

export async function updateTask(
  before: Task,
  input: Partial<TaskInput>,
  actor: Actor,
  users: readonly User[],
  columnName: (id: string) => string,
): Promise<Task> {
  const assignees = input.assigneeIds !== undefined ? resolveAssignees(users, input.assigneeIds, before.assignees) : before.assignees;
  const after: Task = {
    ...before,
    title: input.title !== undefined ? input.title.trim() : before.title,
    description: input.description ?? before.description,
    columnId: input.columnId ?? before.columnId,
    assignees,
    assigneeId: assignees[0]?.id ?? null,
    assigneeName: assignees[0]?.name ?? null,
    priority: input.priority ?? before.priority,
    dueDate: input.dueDate !== undefined ? input.dueDate : before.dueDate,
    labels: input.labels ?? before.labels,
  };
  const diffs = taskDiffs(before, after, columnName);
  if (!diffs.length) return before;
  const row = {
    title: after.title,
    description: after.description,
    column_id: after.columnId,
    ...assigneeColumns(after.assignees),
    priority: after.priority,
    due_date: after.dueDate,
    labels: after.labels,
  };
  let { error } = await supabase().from('tasks').update(row).eq('id', before.id);
  if (error && missingAssigneeArrays(error)) ({ error } = await supabase().from('tasks').update(withoutAssigneeArrays(row)).eq('id', before.id));
  if (error) return fail('updateTask', error);
  await logActivity(before.id, actor, diffs);
  // Only the people newly on the task are told; the ones already on it are not told again.
  const added = after.assignees.filter((a) => !before.assignees.some((b) => b.id === a.id));
  if (added.length) {
    await notify(added.map((a) => ({ userId: a.id, kind: 'assigned' as const })), after, actor, `${actor.name} assigned you "${after.title}"`);
  }
  return after;
}

/** Drop a card into a column at a position (between its new neighbours). */
export async function moveTask(
  task: Task,
  toColumnId: string,
  position: number,
  actor: Actor,
  columnName: (id: string) => string,
): Promise<void> {
  const { error } = await supabase().from('tasks').update({ column_id: toColumnId, position }).eq('id', task.id);
  if (error) return fail('moveTask', error);
  if (toColumnId !== task.columnId) {
    await logActivity(task.id, actor, { kind: 'status', from: columnName(task.columnId), to: columnName(toColumnId) });
  }
}

export async function archiveTask(task: Task, actor: Actor): Promise<void> {
  const { error } = await supabase().from('tasks')
    .update({ archived: true, archived_at: isoFromMs(Date.now()), archived_by: actor.id }).eq('id', task.id);
  if (error) return fail('archiveTask', error);
  await logActivity(task.id, actor, { kind: 'archived', from: null, to: null });
}

export async function restoreTask(task: Task, actor: Actor): Promise<void> {
  const { error } = await supabase().from('tasks')
    .update({ archived: false, archived_at: null, archived_by: null }).eq('id', task.id);
  if (error) return fail('restoreTask', error);
  await logActivity(task.id, actor, { kind: 'restored', from: null, to: null });
}

/** Admin only (RLS enforces it too). Comments, activity and notifications go with it. */
export async function deleteTaskForever(taskId: string): Promise<void> {
  const { error } = await supabase().from('tasks').delete().eq('id', taskId);
  if (error) return fail('deleteTaskForever', error);
}

export async function addComment(task: Task, body: string, actor: Actor, users: readonly User[]): Promise<TaskComment> {
  const text = body.trim();
  const mentioned = extractMentionedUsers(text, users);
  const { data, error } = await supabase().from('task_comments').insert({
    task_id: task.id, author_id: actor.id, author_name: actor.name, body: text, mentions: mentioned.map((u) => u.id),
  }).select('*').single();
  if (error || !data) return fail('addComment', error);
  await logActivity(task.id, actor, { kind: 'comment', from: null, to: text.slice(0, 120) });
  const preview = text.length > 140 ? `${text.slice(0, 140)}...` : text;
  await notify(
    commentRecipients({ authorId: actor.id, assigneeIds: task.assignees.map((a) => a.id), mentionedIds: mentioned.map((u) => u.id) }),
    task, actor, `${actor.name}: ${preview}`,
  );
  return commentFromRow(data);
}

export async function editComment(comment: TaskComment, body: string, users: readonly User[]): Promise<void> {
  const text = body.trim();
  const { error } = await supabase().from('task_comments')
    .update({ body: text, mentions: extractMentionedUsers(text, users).map((u) => u.id), edited_at: isoFromMs(Date.now()) })
    .eq('id', comment.id);
  if (error) return fail('editComment', error);
}

export async function deleteComment(commentId: string): Promise<void> {
  const { error } = await supabase().from('task_comments').delete().eq('id', commentId);
  if (error) return fail('deleteComment', error);
}

// ── Columns (admin) ────────────────────────────────────────────────────────

/** A new column at the end of `columns` — on `boardId` when boards have their own columns (0109). */
export async function createColumn(name: string, columns: readonly TaskColumn[], boardId?: string | null): Promise<void> {
  const position = columns.length ? Math.max(...columns.map((c) => c.position)) + 1 : 0;
  const { error } = await supabase().from('task_columns').insert({ name: name.trim(), position, ...(boardId ? { board_id: boardId } : {}) });
  if (error) return fail('createColumn', error);
}

export async function renameColumn(id: string, name: string): Promise<void> {
  const { error } = await supabase().from('task_columns').update({ name: name.trim() }).eq('id', id);
  if (error) return fail('renameColumn', error);
}

/** Swap a column with its neighbour on one side. */
export async function moveColumn(columns: readonly TaskColumn[], id: string, dir: -1 | 1): Promise<void> {
  const sorted = [...columns].sort((a, b) => a.position - b.position);
  const i = sorted.findIndex((c) => c.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= sorted.length) return;
  const a = sorted[i];
  const b = sorted[j];
  const r1 = await supabase().from('task_columns').update({ position: b.position }).eq('id', a.id);
  if (r1.error) return fail('moveColumn', r1.error);
  const r2 = await supabase().from('task_columns').update({ position: a.position }).eq('id', b.id);
  if (r2.error) return fail('moveColumn', r2.error);
}

/** Only an EMPTY column may go (the caller checks; the FK refuses otherwise). */
export async function deleteColumn(id: string): Promise<void> {
  const { error } = await supabase().from('task_columns').delete().eq('id', id);
  if (error) return fail('deleteColumn', error);
}

// ── Boards (0108, Umer 2026-10-04) ─────────────────────────────────────────
// One board per team, each with its own columns (0109; shared before it); task numbers stay global.
// Who sees a board is the page's rule (boardsFor), not the database's: reads stay open like every
// task table. The database keeps a task in a column of its own board (tasks_fit_column).

/** The columns a board shows, in order: its own, plus any shared one (a column read before 0109). */
export const columnsOfBoard = (columns: readonly TaskColumn[], boardId: string): TaskColumn[] =>
  columns.filter((c) => c.boardId === null || c.boardId === boardId).sort((a, b) => a.position - b.position);

/** "All boards": one column per name, in the order the names first appear; `key` groups a task. */
export const mergedColumnKey = (name: string): string => `name:${name.trim().toLowerCase()}`;
export const mergedColumns = (columns: readonly TaskColumn[]): TaskColumn[] => {
  const seen = new Map<string, TaskColumn>();
  for (const c of [...columns].sort((a, b) => a.position - b.position || a.createdAt - b.createdAt)) {
    const key = mergedColumnKey(c.name);
    if (!seen.has(key)) seen.set(key, { ...c, id: key, boardId: null });
  }
  return [...seen.values()];
};

export const GENERAL_BOARD = 'general';

export interface TaskBoardDef {
  id: string;
  name: string;
  color: BoardColor;
  position: number;
  archived: boolean;
}

/** The colours a board can wear: the tab's dot and the card's stripe in "All boards". */
export const BOARD_COLORS = ['slate', 'blue', 'green', 'amber', 'rose', 'violet', 'teal', 'orange'] as const;
export type BoardColor = (typeof BOARD_COLORS)[number];
export const BOARD_DOT: Record<BoardColor, string> = {
  slate: 'bg-slate-400', blue: 'bg-blue-500', green: 'bg-emerald-500', amber: 'bg-amber-500',
  rose: 'bg-rose-500', violet: 'bg-violet-500', teal: 'bg-teal-500', orange: 'bg-orange-500',
};
const isBoardColor = (v: unknown): v is BoardColor => BOARD_COLORS.includes(v as BoardColor);

export const boardFromRow = (r: any): TaskBoardDef => ({
  id: String(r.id),
  name: r.name ?? '',
  color: isBoardColor(r.color) ? r.color : 'slate',
  position: Number(r.position) || 0,
  archived: !!r.archived,
});

/** Board id -> the ids of the people on it. */
export type BoardMembers = Record<string, string[]>;

export interface BoardsState {
  boards: TaskBoardDef[];
  members: BoardMembers;
}

/** Before 0108 the tables are unknown: the page then works exactly as before, with no boards. */
const missingBoards = (error: { message?: string; code?: string } | null | undefined): boolean =>
  error?.code === '42P01' || error?.code === 'PGRST205' || /task_board/.test(error?.message ?? '');

/** Every board (archived too) and its people. null when 0108 is not on this database yet. */
export async function fetchTaskBoards(): Promise<BoardsState | null> {
  const [b, m] = await Promise.all([
    supabase().from('task_boards').select('*').order('position'),
    supabase().from('task_board_members').select('board_id, user_id'),
  ]);
  if (missingBoards(b.error) || missingBoards(m.error)) return null;
  if (b.error) return fail('fetchTaskBoards', b.error);
  if (m.error) return fail('fetchTaskBoards', m.error);
  const members: BoardMembers = {};
  for (const r of m.data || []) (members[String(r.board_id)] ||= []).push(String(r.user_id));
  return { boards: (b.data || []).map(boardFromRow), members };
}

/** The boards this person sees, in order: the Super Admin every open board, anyone else the open
 *  boards they are on. */
export const boardsFor = (user: Pick<User, 'id' | 'role'> | null | undefined, state: BoardsState): TaskBoardDef[] => {
  if (!user) return [];
  const open = state.boards.filter((b) => !b.archived).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  if (isSuperTier(user.role)) return open;
  return open.filter((b) => (state.members[b.id] || []).includes(user.id));
};

/** The people of one board, from a list of people (assignee picker, @mentions, the Everyone filter). */
export const boardPeople = <U extends Pick<User, 'id'>>(boardId: string, state: BoardsState, people: readonly U[]): U[] => {
  const ids = new Set(state.members[boardId] || []);
  return people.filter((u) => ids.has(u.id));
};

export async function createBoard(name: string, color: BoardColor, boards: readonly TaskBoardDef[], actor: Actor): Promise<TaskBoardDef> {
  const position = boards.reduce((m, b) => Math.max(m, b.position), 0) + 1;
  const { data, error } = await supabase().from('task_boards')
    .insert({ name: name.trim(), color, position, created_by: actor.id }).select('*').single();
  if (error || !data) return fail('createBoard', error);
  return boardFromRow(data);
}

export async function updateBoard(id: string, patch: Partial<Pick<TaskBoardDef, 'name' | 'color' | 'archived'>>): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name.trim();
  if (patch.color !== undefined) row.color = patch.color;
  if (patch.archived !== undefined) row.archived = patch.archived;
  const { error } = await supabase().from('task_boards').update(row).eq('id', id);
  if (error) return fail('updateBoard', error);
}

export async function addBoardMember(boardId: string, userId: string): Promise<void> {
  const { error } = await supabase().from('task_board_members').upsert({ board_id: boardId, user_id: userId }, { onConflict: 'board_id,user_id', ignoreDuplicates: true });
  if (error) return fail('addBoardMember', error);
}

export async function removeBoardMember(boardId: string, userId: string): Promise<void> {
  const { error } = await supabase().from('task_board_members').delete().eq('board_id', boardId).eq('user_id', userId);
  if (error) return fail('removeBoardMember', error);
}

/** Put a task on another board; the diary says from where to where. */
export async function moveTaskToBoard(task: Task, toBoardId: string, actor: Actor, boardName: (id: string) => string): Promise<void> {
  if (task.boardId === toBoardId) return;
  const { error } = await supabase().from('tasks').update({ board_id: toBoardId }).eq('id', task.id);
  if (error) return fail('moveTaskToBoard', error);
  await logActivity(task.id, actor, { kind: 'board', from: boardName(task.boardId), to: boardName(toBoardId) });
}
